'use client';

import type { ClassValue } from 'clsx';
import { clsxm } from '@vs-shop/utils';
import { Option } from 'effect';
import { useRef } from 'react';

import { Url } from '../../../route/router';

import ArticlesGrid from './ArticlesGrid';
import ArticlesLoadNextPage from './ArticlesLoadNextPage';
import ArticlesPagination from './ArticlesPagination';

import { fetchArticlesPageAtom } from '@vs-shop/engine/atom/articles/fetchArticlesPage';
import type { AuthorId, CategoryId } from '@vs-shop/schemas/branded/ContentSchema';
import {
  changeProductPageAtomFn,
  currentPageAtom,
  loadMorePagesAtom,
  pageSearchParamAtom,
} from '@vs-shop/engine/atom/products/pagination';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtomSet, useAtomValue } from '@effect/atom-react';

type Props = {
  className?: ClassValue;
  itemsPerPage: number;
  section: string | undefined;
  categoryId: CategoryId | undefined;
  authorId: AuthorId | undefined;
};

export default function ArticlesOverview({
  className,
  itemsPerPage,
  section,
  categoryId,
  authorId,
}: Props) {
  const currentPage = useAtomValue(currentPageAtom);

  const articlesPerPageResponse = useAtomValue(
    fetchArticlesPageAtom(currentPage, itemsPerPage, section, categoryId, authorId)
  );

  const changePage = useAtomSet(changeProductPageAtomFn, {
    mode: 'promise',
  });

  const changePageSearchParam = useAtomSet(pageSearchParamAtom, {
    mode: 'value',
  });
  const changeLoadMorePages = useAtomSet(loadMorePagesAtom, {
    mode: 'value',
  });

  const loadMorePages = () => {
    changePageSearchParam((currentPageOption) => {
      const currentPage = Option.getOrElse(currentPageOption, () => 1);
      return Option.some(currentPage + 1);
    });
    changeLoadMorePages((loadMorePages) => loadMorePages + 1);
  };

  const ref = useRef<HTMLDivElement>(null);

  function scrollToGrid(): void {
    if (!ref.current) return;

    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'center',
    });
  }

  const searchParams: Record<string, string> = {};
  if (section) {
    searchParams.section = section;
  }

  return (
    <div ref={ref} className={clsxm(className)}>
      <ArticlesGrid
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        section={section}
        categoryId={categoryId}
        authorId={authorId}
      />

      {AsyncResult.builder(articlesPerPageResponse)
        .onSuccess((response) =>
          Option.match(Option.fromNullishOr(response['hydra:view']), {
            onNone: () => null,
            onSome: (hydraViewExtended) => {
              const totalItems = response['hydra:totalItems'];
              if (totalItems > 1) {
                return (
                  <ArticlesPagination
                    hydraViewExtended={hydraViewExtended}
                    changePage={changePage}
                    scrollToGrid={scrollToGrid}
                    baseUrl={Url.articles}
                    searchParams={searchParams}
                  />
                );
              }
              return null;
            },
          })
        )
        .render()}

      {AsyncResult.builder(articlesPerPageResponse)
        .onSuccess((response) =>
          Option.match(Option.fromNullishOr(response['hydra:view']), {
            onNone: () => null,
            onSome: (hydraViewExtended) => (
              <ArticlesLoadNextPage
                hydraViewExtended={hydraViewExtended}
                loadMorePages={loadMorePages}
              />
            ),
          })
        )
        .render()}
    </div>
  );
}
